// BulkActionToolbar.jsx
// Bulk action toolbar for multiple device operations
// Part of Device Management Overhaul - Phase 6: Bulk Actions

import React, { memo, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Checkbox,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  LinearProgress,
  Alert,
  Slide,
  Fade
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Label as TagIcon,
  GetApp as ExportIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  CheckBox as SelectAllIcon,
  CheckBoxOutlineBlank as DeselectIcon,
  Refresh as RefreshIcon,
  PlayArrow as ExecuteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const BulkActionToolbar = memo(({
  selectedDevices,
  totalDevices,
  onSelectAll,
  onDeselectAll,
  onBulkAction,
  locations = [],
  tags = [],
  visible = false
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processProgress, setProcessProgress] = useState(0);

  // Dialog states
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  const handleActionClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle bulk location assignment
  const handleAssignLocation = useCallback(async () => {
    if (!selectedLocation) {
      enqueueSnackbar('Please select a location', { variant: 'warning' });
      return;
    }

    setProcessing(true);
    setProcessProgress(0);

    try {
      const result = await onBulkAction({
        action: 'assign_location',
        deviceIds: selectedDevices,
        locationId: selectedLocation
      });

      // Simulate progress
      const interval = setInterval(() => {
        setProcessProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 10;
        });
      }, 100);

      if (result.success) {
        enqueueSnackbar(
          `Successfully assigned ${selectedDevices.length} devices to location`,
          { variant: 'success' }
        );
        setLocationDialogOpen(false);
        onDeselectAll();
      } else {
        throw new Error(result.error || 'Failed to assign location');
      }
    } catch (error) {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    } finally {
      setProcessing(false);
      setProcessProgress(0);
    }
  }, [selectedLocation, selectedDevices, onBulkAction, enqueueSnackbar, onDeselectAll]);

  // Handle bulk delete
  const handleBulkDelete = useCallback(async () => {
    setConfirmDialog({
      title: 'Delete Devices',
      message: `Are you sure you want to delete ${selectedDevices.length} devices? This action cannot be undone.`,
      severity: 'error',
      action: async () => {
        setProcessing(true);
        try {
          const result = await onBulkAction({
            action: 'delete',
            deviceIds: selectedDevices
          });

          if (result.success) {
            enqueueSnackbar(
              `Successfully deleted ${selectedDevices.length} devices`,
              { variant: 'success' }
            );
            onDeselectAll();
          } else {
            throw new Error(result.error || 'Failed to delete devices');
          }
        } catch (error) {
          enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
        } finally {
          setProcessing(false);
          setConfirmDialog(null);
        }
      }
    });
  }, [selectedDevices, onBulkAction, enqueueSnackbar, onDeselectAll]);

  // Handle bulk export
  const handleBulkExport = useCallback(async () => {
    setProcessing(true);
    try {
      const result = await onBulkAction({
        action: 'export',
        deviceIds: selectedDevices,
        format: 'csv'
      });

      if (result.success && result.data) {
        // Create download link
        const blob = new Blob([result.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `devices_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        enqueueSnackbar(
          `Successfully exported ${selectedDevices.length} devices`,
          { variant: 'success' }
        );
      } else {
        throw new Error(result.error || 'Failed to export devices');
      }
    } catch (error) {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    } finally {
      setProcessing(false);
    }
  }, [selectedDevices, onBulkAction, enqueueSnackbar]);

  // Handle bulk tagging
  const handleAssignTags = useCallback(async () => {
    if (selectedTags.length === 0) {
      enqueueSnackbar('Please select at least one tag', { variant: 'warning' });
      return;
    }

    setProcessing(true);
    try {
      const result = await onBulkAction({
        action: 'assign_tags',
        deviceIds: selectedDevices,
        tags: selectedTags
      });

      if (result.success) {
        enqueueSnackbar(
          `Successfully tagged ${selectedDevices.length} devices`,
          { variant: 'success' }
        );
        setTagDialogOpen(false);
        onDeselectAll();
      } else {
        throw new Error(result.error || 'Failed to assign tags');
      }
    } catch (error) {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    } finally {
      setProcessing(false);
    }
  }, [selectedTags, selectedDevices, onBulkAction, enqueueSnackbar, onDeselectAll]);

  if (!visible || selectedDevices.length === 0) return null;

  return (
    <>
      <Fade in={visible && selectedDevices.length > 0}>
        <Paper
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            p: 2,
            mb: 2,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            transition: 'all 0.3s ease'
          }}
          TransitionComponent={Transition}
        >
          {/* Selection info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={selectedDevices.length === totalDevices}
              indeterminate={selectedDevices.length > 0 && selectedDevices.length < totalDevices}
              onChange={(e) => {
                if (e.target.checked) {
                  onSelectAll();
                } else {
                  onDeselectAll();
                }
              }}
              sx={{ color: 'primary.contrastText' }}
            />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {selectedDevices.length} of {totalDevices} selected
            </Typography>
          </Box>

          {/* Quick actions */}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<LocationIcon />}
              onClick={() => setLocationDialogOpen(true)}
              disabled={processing}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              Assign Location
            </Button>

            <Button
              variant="contained"
              size="small"
              startIcon={<TagIcon />}
              onClick={() => setTagDialogOpen(true)}
              disabled={processing}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              Tag
            </Button>

            <Button
              variant="contained"
              size="small"
              startIcon={<ExportIcon />}
              onClick={handleBulkExport}
              disabled={processing}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              Export
            </Button>

            <Button
              variant="contained"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={handleActionClick}
              disabled={processing}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              More Actions
            </Button>

            <IconButton
              size="small"
              onClick={onDeselectAll}
              sx={{ color: 'primary.contrastText' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {processing && (
            <LinearProgress
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 2
              }}
              variant={processProgress > 0 ? 'determinate' : 'indeterminate'}
              value={processProgress}
            />
          )}
        </Paper>
      </Fade>

      {/* More actions menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => {
          handleClose();
          // Implement bulk update
        }}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText primary="Bulk Update" />
        </MenuItem>

        <MenuItem onClick={() => {
          handleClose();
          // Implement refresh status
        }}>
          <ListItemIcon>
            <RefreshIcon />
          </ListItemIcon>
          <ListItemText primary="Refresh Status" />
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => {
          handleClose();
          handleBulkDelete();
        }}>
          <ListItemIcon>
            <DeleteIcon color="error" />
          </ListItemIcon>
          <ListItemText primary="Delete Selected" />
        </MenuItem>
      </Menu>

      {/* Location assignment dialog */}
      <Dialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Location to {selectedDevices.length} Devices</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Location</InputLabel>
            <Select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              label="Select Location"
            >
              {locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.friendlyName || location.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssignLocation}
            variant="contained"
            disabled={!selectedLocation || processing}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag assignment dialog */}
      <Dialog
        open={tagDialogOpen}
        onClose={() => setTagDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Tag {selectedDevices.length} Devices</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Tags</InputLabel>
            <Select
              multiple
              value={selectedTags}
              onChange={(e) => setSelectedTags(e.target.value)}
              label="Select Tags"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {tags.map((tag) => (
                <MenuItem key={tag} value={tag}>
                  {tag}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssignTags}
            variant="contained"
            disabled={selectedTags.length === 0 || processing}
          >
            Apply Tags
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog */}
      {confirmDialog && (
        <Dialog
          open={Boolean(confirmDialog)}
          onClose={() => !processing && setConfirmDialog(null)}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color={confirmDialog.severity || 'warning'} />
              {confirmDialog.title}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity={confirmDialog.severity || 'warning'}>
              {confirmDialog.message}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setConfirmDialog(null)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDialog.action}
              variant="contained"
              color={confirmDialog.severity || 'warning'}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
});

BulkActionToolbar.displayName = 'BulkActionToolbar';

export default BulkActionToolbar;